import { useEffect, useState } from 'react';
import { AlertCircle, Check, Clock, UserCircle2, X } from 'lucide-react';
import { Avatar } from '../ui/Avatar';
import styles from './PacienteAsignadoCard.module.css';

export type PrioridadConsulta = 'normal' | 'alta' | 'urgente';

export interface PacienteAsignadoCardProps {
  /** Patient full name */
  pacienteNombre: string;
  /** Patient age (optional) */
  pacienteEdad?: number | null;
  /** Patient sex (optional) — e.g. "Masculino", "Femenino" */
  pacienteSexo?: string | null;
  /** Patient profile photo (optional) — falls back to initials */
  pacienteFotoURL?: string | null;
  /** Brief reason / triage summary */
  motivoConsulta?: string;
  /** Priority for visual emphasis */
  prioridad?: PrioridadConsulta;
  /** Wait time in minutes since patient entered the queue */
  esperaMin?: number;
  /** Seconds remaining for the medico to accept before auto-reassign */
  countdownSec?: number;
  /** Called when medico accepts the patient */
  onAccept: () => void | Promise<void>;
  /** Called when medico rejects */
  onReject: () => void | Promise<void>;
  /** Disable controls while a server call is in-flight */
  loading?: boolean;
}

/**
 * Notification card shown to the médico when the system auto-assigns
 * a patient in hybrid mode. The médico can accept, reject, or wait
 * for the countdown to expire (which counts as implicit rejection).
 */
export function PacienteAsignadoCard({
  pacienteNombre,
  pacienteEdad,
  pacienteSexo,
  pacienteFotoURL,
  motivoConsulta,
  prioridad = 'normal',
  esperaMin,
  countdownSec,
  onAccept,
  onReject,
  loading = false,
}: PacienteAsignadoCardProps) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(
    countdownSec ?? null,
  );

  useEffect(() => {
    if (countdownSec === undefined) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSecondsLeft(countdownSec);
  }, [countdownSec]);

  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => (s ?? 0) - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const isUrgent = prioridad === 'urgente';
  const isHigh = prioridad === 'alta';

  return (
    <div
      role="alertdialog"
      aria-labelledby="paciente-asignado-title"
      aria-describedby="paciente-asignado-desc"
      className={`${styles.card} ${isUrgent ? styles.urgent : isHigh ? styles.high : ''}`}
    >
      <header className={styles.header}>
        <span className={styles.eyebrow}>
          <span className={styles.pulse} aria-hidden="true" />
          Paciente asignado
        </span>
        {prioridad !== 'normal' && (
          <span
            className={`${styles.priorityBadge} ${
              isUrgent ? styles.priorityUrgent : styles.priorityHigh
            }`}
          >
            <AlertCircle size={11} aria-hidden="true" />
            {isUrgent ? 'Urgente' : 'Prioridad alta'}
          </span>
        )}
      </header>

      <div className={styles.body}>
        <Avatar
          className={styles.avatar}
          nombre={pacienteNombre}
          fotoURL={pacienteFotoURL}
          fallback={<UserCircle2 size={26} />}
          zoomable
        />
        <div className={styles.info}>
          <h3 id="paciente-asignado-title" className={styles.name}>
            {pacienteNombre}
            {(pacienteEdad != null || pacienteSexo) && (
              <span className={styles.age}>
                {' · '}
                {[
                  pacienteEdad != null ? `${pacienteEdad} años` : null,
                  pacienteSexo || null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          </h3>
          {motivoConsulta && (
            <p id="paciente-asignado-desc" className={styles.reason}>
              {motivoConsulta}
            </p>
          )}
          <div className={styles.meta}>
            {esperaMin != null && (
              <span className={styles.metaItem}>
                <Clock size={11} aria-hidden="true" />
                Esperando {esperaMin} min
              </span>
            )}
            {secondsLeft != null && secondsLeft > 0 && (
              <span className={styles.metaItem}>
                <Clock size={11} aria-hidden="true" />
                Reasignación automática en{' '}
                <strong className={styles.countdown}>{secondsLeft}s</strong>
              </span>
            )}
          </div>
        </div>
      </div>

      <footer className={styles.actions}>
        <button
          type="button"
          className={styles.btnReject}
          onClick={onReject}
          disabled={loading}
        >
          <X size={14} aria-hidden="true" />
          Rechazar
        </button>
        <button
          type="button"
          className={styles.btnAccept}
          onClick={onAccept}
          disabled={loading}
        >
          <Check size={14} aria-hidden="true" />
          Atender paciente
        </button>
      </footer>
    </div>
  );
}
